import {
  COMPONENT_REPORT,
  DOWNLOAD_FILE_NAME,
  OPERATION_STATUS,
  REPORT_KEYS,
  SNAPSHOT_ID,
  MAX_RETRIES,
  TOGGLE_PLACEMENT,
  MAX_PAYLOADS_THRESHOLD,
} from './common/constants';
import { COMPONENT_TO_LINK } from '../../constants/urls';

import { formatHeadingDisplay, formatCellDisplay, getFormat } from './utils/formatting';
import { zip, isEmpty } from './utils';

import {
  toggleStatus,
  setChildrenVisibility,
  allChildrenLoaded,
  EXPANSION_STATUS,
  toggleLoading,
} from './utils/toggle';
import { accuracyDataWithCompositeKeys, replaceLiveSnapshotId, transformToReportFormat } from './utils/transformations';
import { getRowsWithOppuHierarchy, findRowIndex, findChildRowIndex, OPPU_GROUPING_ITEMS } from './utils/oppu_hierarchy';
import { buildContentSelectorOption, findColumnSetItems } from './utils/report_preferences';
import { getNonAggregatedFieldsWithMultiValues } from '../../utils/payload';
import { pullDataWithRetryAndTimeout } from './utils/load_data';
import { deriveComponentKey } from '../../utils/components';
import { reportDataByColumn, reportDataByRow } from './utils/download';
import { getCompositeKeys, getForEachCompositeKey } from './utils/composite_keys';

import ComponentCellDisplay from './components/component_cell_display';
import { ReportTable } from './components/report_table';
import DownloadReportDataWindow from './components/download_report_data_window';
import { ThresholdCrossedMessage } from './components/threshold_crossed_message';

import { REPORT_TYPE } from '../../constants/report_type';

export class ComponentReport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      visibleRowsChanged: false,
      downloadDataWindowVisible: false,
      selectedColumnsByUser: [],
      thresholdCrossedMessageDismissed: false,
    };
    this.reportData = null;
    this.contentSelectorOptions = null;
  }

  componentDidUpdate = (prevProps) => {
    if (
      prevProps.downloadStatus !== this.props.downloadStatus &&
      this.props.downloadStatus === OPERATION_STATUS.IN_PROGRESS
    ) {
      this.beginDownloadData();
    }

    if (prevProps.selectedColumnSetsByUser !== this.props.selectedColumnSetsByUser) {
      this.regenerateContentSelectorOptions([], true);
    }
  };

  beginDownloadData = () => {
    const { requestPayloads } = this.props;
    const aggregatedFields = getNonAggregatedFieldsWithMultiValues(requestPayloads);
    if (aggregatedFields.length > 0) {
      this.setState({ downloadDataWindowVisible: true });
    } else {
      this.downloadData();
    }
  };

  onDownloadDataWindowCancel = () => {
    this.setState({ downloadDataWindowVisible: false });
    this.props.onDownloadDataCancel();
  };

  onDownloadDataWindowSubmit = (downloadType) => {
    this.setState({ downloadDataWindowVisible: false });

    const dataToDownload = downloadType === 'rows' ? this.getDownloadDataByRow() : this.getDownloadDataByColumn();

    this.downloadData(dataToDownload);
  };

  getDownloadDataByColumn = () => {
    return reportDataByColumn(this.reportData, OPPU_GROUPING_ITEMS);
  };

  getDownloadDataByRow = () => {
    return reportDataByRow(this.reportData, OPPU_GROUPING_ITEMS);
  };

  downloadData = (dataToDownload = this.getDownloadDataByColumn()) => {
    this.props.downloadFile(dataToDownload.join('\n'), DOWNLOAD_FILE_NAME);
    this.props.onDownloadDataSuccess();
  };

  parseData = () => {
    const { requestPayloads, responsePayloads, liveSnapshot } = this.props;

    const reportData = this.parsePayloadData(requestPayloads, responsePayloads, liveSnapshot);

    const { reportKeys, reportHeadings, compositeKeys } = reportData;

    const requiredChannels = [...new Set(requestPayloads.map((r) => r.channel))];

    const reportRows =
      reportData.reportRows && reportData.reportRows.length
        ? getRowsWithOppuHierarchy(reportData.reportRows, this.props.oppuHierarchy, requiredChannels)
        : [];

    return { reportKeys, reportHeadings, reportRows, compositeKeys };
  };

  parsePayloadData = (requestPayloads, responsePayloads, liveSnapshot) => {
    const aggregatedFields = getNonAggregatedFieldsWithMultiValues(requestPayloads);

    const dataSetKeys = [SNAPSHOT_ID].concat(aggregatedFields || []);

    let compositeKeys = getCompositeKeys(replaceLiveSnapshotId(requestPayloads, liveSnapshot), dataSetKeys);
    let accuracyData = accuracyDataWithCompositeKeys(responsePayloads, compositeKeys, COMPONENT_REPORT.DEDUPED_COL_KEYS);

    const sortedHeadings = [...new Set(responsePayloads.map((p) => p.sortedHeadings).flat())];
    const sortedKeys = [...new Set(responsePayloads.map((p) => p.sortedKeys).flat())];

    const sortedHeadingsWithCompositeKeys = getForEachCompositeKey(sortedHeadings, compositeKeys);
    const sortedKeysWithCompositeKeys = getForEachCompositeKey(sortedKeys, compositeKeys);

    const transformedData = transformToReportFormat(
      sortedKeysWithCompositeKeys,
      sortedHeadingsWithCompositeKeys,
      accuracyData,
      COMPONENT_REPORT.DEDUPED_COL_KEYS
    );

    return { ...transformedData, compositeKeys };
  };

  onToggleExpand = (event, item) => {
    const { children } = item;
    const { reportRows } = this.reportData;

    const childrenLoaded = children && children.length && allChildrenLoaded(children, reportRows);
    if (!childrenLoaded) {
      // Set the parent item status to 'loading' state
      // and call a set state to refresh the view
      toggleLoading(item);
      this.setState({ visibleRowsChanged: true });

      // Load the children and reset the 'loading' state
      // of the parent item.
      this.loadChildren(item);
      toggleLoading(item);
    } else {
      this.toggleItem(item);
      this.setState({ visibleRowsChanged: true });
    }
  };

  toggleItem = (item) => {
    const { reportRows } = this.reportData;
    toggleStatus(item);
    if (item.children) {
      const isVisible = item.status === EXPANSION_STATUS.COLLAPSE;
      setChildrenVisibility(item.children, isVisible, reportRows);
    }
  };

  loadChildren = (item) => {
    let childrenRequestPayloads = this.childrenRequestLoads(item);
    let childrenResponsePayloads = [];
    let childrenIncompleteResponsePayloads = [];

    const onAllPayloadsLoaded = (allPayloadsLoaded) => {
      // Pass
    };

    const { reportRows } = this.reportData;

    const onLoadDone = (onLoadDone) => {
      const childrenData = this.parsePayloadData(
        childrenRequestPayloads,
        childrenResponsePayloads,
        this.props.liveSnapshot
      );
      const { reportRows: childrenReportRows } = childrenData;

      for (let child of item.children) {
        const existingRowIndex = findChildRowIndex(child, reportRows);
        if (existingRowIndex > -1) {
          const childChannel = child.split('/')[0];
          const childComponent = `${item.component}/${child.split('/')[1]}`;
          const updatedRowIndex = findRowIndex(childrenReportRows, childComponent, childChannel);

          reportRows[existingRowIndex] = {
            ...childrenReportRows[updatedRowIndex],
            ...reportRows[existingRowIndex],
            loaded: true,
            component: reportRows[existingRowIndex].component,
            parentComponent: item.component,
          };
        }
      }

      this.toggleItem(item);
      this.setState({ visibleRowsChanged: true });
    };

    for (let i = 0; i < childrenRequestPayloads.length; i++) {
      pullDataWithRetryAndTimeout(
        i,
        0,
        this.props.client,
        childrenRequestPayloads,
        childrenResponsePayloads,
        childrenIncompleteResponsePayloads,
        onAllPayloadsLoaded,
        onLoadDone
      );
    }
  };

  childrenRequestLoads = (item) => {
    return this.props.requestPayloads.map((r) => {
      return { ...r, channel: item.channel, parentComponent: deriveComponentKey(item.component) };
    });
  };

  getColumnDefinitions = (reportData) => {
    const { reportKeys, reportHeadings } = reportData;
    const { columnFormats, displayInMillions, requestPayloads } = this.props;

    const requiredChannels = [...new Set(requestPayloads.map((r) => r.channel.toLowerCase()))];

    let columns = [];

    for (let [key, heading] of zip(reportKeys, reportHeadings)) {
      let columnEntry = {};
      columnEntry['id'] = key;
      columnEntry['header'] = () => formatHeadingDisplay(heading, getFormat(key, columnFormats));
      columnEntry['minWidth'] =
        COMPONENT_REPORT.WRAP_LINE_COLUMNS.includes(key) ||
        COMPONENT_REPORT.WRAP_LINE_COLUMNS.some((c) => key.endsWith(c))
          ? '200px'
          : '100px';
      columnEntry['allowLineWrap'] =
        COMPONENT_REPORT.WRAP_LINE_COLUMNS.includes(key) ||
        COMPONENT_REPORT.WRAP_LINE_COLUMNS.some((c) => key.endsWith(c));
      columnEntry['cell'] = (item) => {
        return key === REPORT_KEYS.COMPONENT ? (
          <ComponentCellDisplay
            item={item}
            onToggleExpand={this.onToggleExpand}
            mapping={COMPONENT_TO_LINK}
            requiredChannels={requiredChannels}
          />
        ) : (
          formatCellDisplay(item[key], getFormat(key, columnFormats), displayInMillions, item)
        );
      };

      columns.push(columnEntry);
    }

    return columns;
  };

  onToggleMillions = () => {
    this.props.onToggleMillions();
  };

  onContentSelectionChange = (event) => {
    const contentSelection = event.detail.contentSelection;
    const { selectedColumnsByUser } = this.state;

    const selectionChanged = [...new Set(contentSelection)] !== [...new Set(selectedColumnsByUser)];

    if (selectionChanged) {
      this.setState({ selectedColumnsByUser: contentSelection });

      const { reportKeys, reportHeadings } = this.reportData;
      this.contentSelectorOptions = this.getContentSelectorOptions(
        reportKeys,
        reportHeadings,        
        contentSelection,
        false
      );

      if (!this.isThresholdCrossed()) {
        const columnSets = this.props.selectedColumnSetsByUser || this.props.selectedColumnSets;
        this.props.onContentSelectionChange(event, columnSets);
      }
    }
  };

  onContentSelectorOptionsRegenerate = () => {
    this.props.onContentSelectorOptionsRegenerate(this.contentSelectorOptions);
  };

  regenerateContentSelectorOptions = (selectedColumnsByUser = [], columnSetChanged = false) => {
    const { reportKeys, reportHeadings } = this.reportData;
    this.contentSelectorOptions = this.getContentSelectorOptions(
      reportKeys,
      reportHeadings,      
      selectedColumnsByUser,
      columnSetChanged
    );
    this.onContentSelectorOptionsRegenerate();
  };

  getContentSelectorOptions = (reportKeys, reportHeadings, selectedColumnsByUser = [], columnSetChanged = false) => {
    const { reportPreferences, columnSets, selectedColumnSetsByUser, columnsJson } = this.props;

    const combinedColumnSets = {
      ...columnSets,
      selectedColumnSets: selectedColumnSetsByUser
        ? findColumnSetItems(columnsJson, selectedColumnSetsByUser, REPORT_TYPE.COMPONENT)
        : columnSets.selectedColumnSets,
      selectedColumnsByUser,
      columnSetChanged,
      columnsForPayloadsThresholdCrossed: this.isThresholdCrossed()
        ? columnsJson.columnsForPayloadsThresholdCrossed
        : null,
    };

    return [
      {
        options: reportKeys.map((key, i) =>
          buildContentSelectorOption(
            key,
            reportHeadings[i],
            combinedColumnSets,
            reportPreferences,
            REPORT_TYPE.COMPONENT
          )
        ),
      },
    ];
  };

  isThresholdCrossed = () => {
    const { compositeKeys } = this.reportData;
    return compositeKeys.length > MAX_PAYLOADS_THRESHOLD;
  };

  onThresholdCrossedMessageDismissed = () => {
    this.setState({ thresholdCrossedMessageDismissed: true });
  };

  render() {
    const { loading, displayInMillions, updateReportData } = this.props;
    const { visibleRowsChanged, downloadDataWindowVisible, selectedColumnsByUser } = this.state;

    const isReportEmpty = isEmpty(this.reportData);

    if (isReportEmpty) {
      this.reportData = this.parseData();
      updateReportData(this.reportData);
      this.regenerateContentSelectorOptions(selectedColumnsByUser);
    }

    const { reportRows } = this.reportData;

    let visibleReportRows = reportRows.filter((r) => r.visible);
    if (visibleRowsChanged) {
      visibleReportRows = reportRows.filter((r) => r.visible);
    }

    return (
      <div>
        {downloadDataWindowVisible && (
          <DownloadReportDataWindow
            onCancel={this.onDownloadDataWindowCancel}
            onSubmit={this.onDownloadDataWindowSubmit}
          />
        )}

        {
          <ThresholdCrossedMessage
            visible={!this.state.thresholdCrossedMessageDismissed && this.isThresholdCrossed()}
            onDismiss={this.onThresholdCrossedMessageDismissed}
          />
        }

        <ReportTable
          loading={loading}
          displayInMillions={displayInMillions}
          reportRows={visibleReportRows}
          columnDefinitions={this.getColumnDefinitions(this.reportData)}
          contentSelectorOptions={this.contentSelectorOptions}
          onContentSelectionChange={this.onContentSelectionChange}
          onToggleMillions={this.onToggleMillions}
          togglePlacement={TOGGLE_PLACEMENT.IN_HEADER}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/reports/component_report.js